import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import FileUpload from 'components/doc-components/FileUpload';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../src/config/firebase';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import categories from '../../data/categories';
import audiences from '../../config/audiences';

const EditDocument = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [fileUrl, setFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category_id: '888',
    image_file: null,
    image_filename: null,
    user_id: null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const id = window.location.pathname.split('/').pop();
      if (id) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(`${apiUrl}/documents/id/${id}`);

          if (response.data) {
            // console.log('responded', response.data);
            const eventData = response.data.data[0];
            // console.log('eventData', eventData);

            setFormData({
              title: eventData.title,
              image_file: eventData.image_file,
              image_filename: eventData.filename,
              doc_url: eventData.doc_url,
              user_id: eventData.user_id,
              category_id: eventData.category_id,
              category: eventData.category,
              date_added: eventData.date_added,
              year_added: eventData.year_added,
              audience: eventData.audience,
            });
            setFileUrl(eventData.doc_url);
          }
        } catch (err) {
          console.log('Error', err);
        } finally {
          setLoading(false);
        }
      } else {
        navigate('/documents/list');
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const category = categories.filter(
      (category) => category.category_id === Number(formData.category_id)
    )[0];

    try {
      let formData2 = new FormData();
      formData2.append('title', formData.title);
      formData2.append('category_id', formData.category_id);
      formData2.append('category', category.dir_name);
      formData2.append('user_id', formData.user_id);
      formData2.append('date_updated', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append('date_added', formData.date_added);
      formData2.append(
        'year_added',
        new Date(formData.date_added).getFullYear()
      );
      formData2.append('image_filename', fileUrl);
      formData2.append('doc_url', fileUrl);
      formData2.append('fileUrl', fileUrl);
      formData2.append('audience', formData.audience);

      const apiUrl = `${process.env.REACT_APP_API_URL}`;

      const id = window.location.pathname.split('/').pop();
      const response = await axios.put(
        `${apiUrl}/documents/id/${id}`,
        formData2
      );

      if (response) {
        toast.success(`Document updated successfully`, {
          theme: 'colored',
        });

        navigate('/documents/list');
      }
    } catch (err) {
      console.log('Error', err.message);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'date_added' || name === 'date_updated') {
      const dateValue = moment.utc(value).format('YYYY-MM-DD HH:mm');
      setFormData({
        ...formData,
        [name]: dateValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title='Update a document'
        description='Load PDF file for Bills, Hansard etc'
        className='mb-3'
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title='Details'
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        {!loading && (
          <Form onSubmit={handleSubmit}>
            <FalconComponentCard.Body>
              <Form.Label>Title</Form.Label>
              <Form.Control
                size='lg'
                type='text'
                placeholder=''
                name='title'
                onChange={handleFieldChange}
                value={formData.title}
              />
              <br />

              <Row>
                <Col lg='12'>
                  <Link to={formData.doc_url} target='_blank'>
                    Current PDF: {formData.image_filename}
                  </Link>
                </Col>
              </Row>

              <FileUpload
                category={formData.category}
                setFileUrl={setFileUrl}
              />

              <Row>
                <Col lg='6'>
                  <Form.Group>
                    <Form.Label>Audience</Form.Label>
                    <Form.Select
                      size='md'
                      className='mb-3'
                      name='audience'
                      onChange={handleFieldChange}
                      value={formData.audience}
                    >
                      {audiences.map((audience) => (
                        <option key={audience.value} value={audience.value}>
                          {audience.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg='6'>
                  <Form.Group>
                    <Form.Label>Document category</Form.Label>
                    <Form.Select
                      size='md'
                      className='mb-3'
                      name='category_id'
                      defaultValue={formData.category_id}
                      onChange={handleFieldChange}
                    >
                      {categories.map((category) => (
                        <option
                          key={category.category_id}
                          value={category.category_id}
                        >
                          {category.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg='6' className='mt-4 mb-3 ml-3'>
                  <Form.Group>
                    <IconButton
                      className='mb-1'
                      variant='falcon-default'
                      size='lg'
                      icon='plus'
                      transform='shrink-3'
                      type='submit'
                    >
                      Submit
                    </IconButton>
                  </Form.Group>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </Form>
        )}
      </FalconComponentCard>
    </>
  );
};

export default EditDocument;
